import {
  AMERICAN_EXPRESS_LOGO_PATH,
  DUITNOW_LOGO_PATH,
  GRAB_PAY_LOGO_PATH,
  MASTERCARD_LOGO_PATH,
  SHOPEE_PAY_LOGO_PATH,
  TNG_EWALLET_LOGO_PATH,
  UNION_PAY_LOGO,
  VISA_LOGO2_PATH
} from './logo';
import type { PaymentMethodIcon } from '../types/payment-method-display';

/**
 * Where a logo sits. `light`: a white surface (the activity list row, the transaction detail
 * box, the registration options). `brand`: a saved-method card face, which is filled with a
 * brand colour, so some logos there are white-only and would vanish on white.
 */
export type LogoSurface = 'light' | 'brand';

const PUBLIC = process.env.PUBLIC_URL || '';

/**
 * The asset for each payment-method logo key, per surface. null means the portal has no
 * logo of its own for that key, and a generic glyph is shown instead.
 */
const PAYMENT_METHOD_ICON_ASSETS: Readonly<
  Record<PaymentMethodIcon, Readonly<Record<LogoSurface, string | null>>>
> = {
  visa: {
    light: `${PUBLIC}/assets/logos/Visa_Logo_2.png`,
    brand: VISA_LOGO2_PATH
  },
  mastercard: {
    light: `${PUBLIC}/assets/logos/MASTERCARD-logo.png`,
    brand: MASTERCARD_LOGO_PATH
  },
  amex: {
    light: `${PUBLIC}/assets/logos/american_express.png`,
    brand: AMERICAN_EXPRESS_LOGO_PATH
  },
  unionpay: {
    light: `${PUBLIC}/assets/logos/union_pay.svg`,
    brand: UNION_PAY_LOGO
  },
  jcb: { light: null, brand: null },
  card: { light: null, brand: null },
  tng: {
    light: `${PUBLIC}/assets/logos/TNG-EWALLET-logo.png`,
    brand: TNG_EWALLET_LOGO_PATH
  },
  grabpay: {
    light: `${PUBLIC}/assets/logos/grab-pay.jpeg`,
    brand: GRAB_PAY_LOGO_PATH
  },
  shopeepay: {
    light: `${PUBLIC}/assets/logos/shopeepay2.svg`,
    brand: SHOPEE_PAY_LOGO_PATH
  },
  qrph: { light: null, brand: null },
  // The card face puts this logo on its own white badge, so one asset serves both.
  duitnow: { light: DUITNOW_LOGO_PATH, brand: DUITNOW_LOGO_PATH },
  pay_separately: { light: null, brand: null },
  link: { light: null, brand: null },
  loyalty: { light: null, brand: null },
  generic: { light: null, brand: null }
};

/**
 * The logo asset for an icon key on a surface, or null when a generic glyph should be shown
 * (no asset for the key, or a key this build does not know yet).
 */
export const paymentMethodIconAsset = (
  icon: string | null | undefined,
  surface: LogoSurface
): string | null =>
  icon && Object.prototype.hasOwnProperty.call(PAYMENT_METHOD_ICON_ASSETS, icon)
    ? PAYMENT_METHOD_ICON_ASSETS[icon as PaymentMethodIcon][surface]
    : null;

/** An asset this build is known to have; for tables that always show a logo. */
export const knownIconAsset = (
  icon: PaymentMethodIcon,
  surface: LogoSurface
): string => PAYMENT_METHOD_ICON_ASSETS[icon][surface] ?? '';

const CARD_GLYPH_ICONS: ReadonlySet<string> = new Set([
  'card',
  'jcb',
  'visa',
  'mastercard',
  'amex',
  'unionpay'
]);

/**
 * The generic glyph for an icon with no asset: a card for a card, a neutral wallet for every
 * other method, so a QR, cash or loyalty payment never shows a credit card.
 */
export const genericGlyphOf = (
  icon: string | null | undefined
): 'card' | 'wallet' =>
  icon && CARD_GLYPH_ICONS.has(icon) ? 'card' : 'wallet';
